import { useMemo } from "react";

import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Tab from "@mui/material/Tab";

import {
  CHART_NOTES_STYLE_TAB_LIST_SCROLL_SX,
  CHART_NOTES_STYLE_TAB_LIST_SX,
  ChartNotesStyleTabLabel
} from "components/chart-notes-style-tabs";
import useAudiologyReportSummaryNote from "features/medical-scribe/hooks/use-audiology-report-summary-note.hook";
import useLatestScribeNote from "features/medical-scribe/hooks/use-latest-scribe-note.hook";
import { AMBIENT_SCRIBE_NOTE_PARENT_TYPE } from "services/types/ambient-scribe-note.type";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

import type { DetailTabId } from "./hooks/use-scribe-detail-tabs.hook";
import { hasScribeTabContent } from "./scribe-tab-content.util";

const DETAIL_TAB_LABELS: Record<DetailTabId, string> = {
  soap: "Scribe",
  context: "Scribe Context",
  transcript: "Transcript",
  summary: "Summary",
  avs: "After Visit Summary",
  debrief: "Debrief",
  salesCoach: "Sales Coach Review",
  events: "Logs"
};

type ScribeDetailTabBarProps = {
  remote: AmbientScribe;
  visibleTabs: DetailTabId[];
  tabView: string;
  onTabChange: (tab: string) => void;
  isAudiologyMode: boolean;
  audiologyReportId: number | undefined;
  isAvsEnabled: boolean;
  salesCoachReviewId: number | null;
};

/**
 * The detail tabs, each with a dot when it holds something to read.
 *
 * Every content value is HTML, so the dots go through `hasScribeTabContent` rather than plain
 * truthiness — `"<p></p>"` is a truthy string that renders as an empty pane. `events` (Logs) is
 * explicitly `false`: it is an internal tab and always populated, so a dot there says nothing.
 */
export default function ScribeDetailTabBar({
  remote,
  visibleTabs,
  tabView,
  onTabChange,
  isAudiologyMode,
  audiologyReportId,
  isAvsEnabled,
  salesCoachReviewId
}: ScribeDetailTabBarProps) {
  // SN-19067 put the audiology summary in `ai_ambientscribe_notes`, never on the scribe, so the
  // Scribe tab's dot reads it there in audiology mode. No extra request: Insert Scribe and the
  // tab itself subscribe with identical args, which is what this hook exists to guarantee.
  const { latestSucceeded: audiologySummaryNote } =
    useAudiologyReportSummaryNote({
      scribeId: remote.id,
      reportId: audiologyReportId,
      enabled: isAudiologyMode
    });
  // The After Visit Summary tab subscribes with identical args (`withPrompt: true`).
  const { latestSucceeded: afterVisitSummaryNote } = useLatestScribeNote({
    scribeId: remote.id,
    parentType: AMBIENT_SCRIBE_NOTE_PARENT_TYPE.AFTER_VISIT_SUMMARY,
    enabled: isAvsEnabled,
    withPrompt: true
  });

  // Depends on the narrow fields, not on `remote`: the detail query polls every 2500 ms while
  // generating, and a whole-object dep would recompute on every tick.
  const tabsWithContent = useMemo<Record<DetailTabId, boolean>>(
    () => ({
      soap: hasScribeTabContent(
        isAudiologyMode ? audiologySummaryNote?.notes : remote.soap_notes
      ),
      context: hasScribeTabContent(remote.custom_context),
      // The backend's own flag, which the transcript tab and its latch already trust.
      transcript: Boolean(remote.has_transcription),
      summary: hasScribeTabContent(remote.summary),
      avs: hasScribeTabContent(afterVisitSummaryNote?.notes),
      // Present without content (the generate CTA states, D2 revised), so this dot means
      // something: on = a debrief exists to look at, off = it still needs action.
      debrief: Boolean(remote.consult_debrief_id),
      // Existence gates this tab, so the dot is on whenever the tab is — but it goes off if the
      // row stops resolving and the latch keeps the tab in place.
      salesCoach: salesCoachReviewId !== null,
      events: false
    }),
    [
      isAudiologyMode,
      audiologySummaryNote?.notes,
      afterVisitSummaryNote?.notes,
      remote.soap_notes,
      remote.custom_context,
      remote.has_transcription,
      remote.summary,
      remote.consult_debrief_id,
      salesCoachReviewId
    ]
  );

  return (
    <TabContext value={tabView}>
      <TabList
        onChange={(_, value) => onTabChange(value)}
        sx={{
          ...CHART_NOTES_STYLE_TAB_LIST_SX,
          ...CHART_NOTES_STYLE_TAB_LIST_SCROLL_SX,
          mt: 2,
          flexShrink: 0
        }}
      >
        {visibleTabs.map((tabId) => (
          <Tab
            key={tabId}
            value={tabId}
            label={
              <ChartNotesStyleTabLabel
                label={DETAIL_TAB_LABELS[tabId]}
                isActive={tabView === tabId}
                hasContent={tabsWithContent[tabId]}
              />
            }
          />
        ))}
      </TabList>
    </TabContext>
  );
}
